import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listSubjects, listTimetableWeeks, getPeriodRules, setPeriodRules, applyPeriodRules } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// Trang "Số tiết": quy định số tiết/tuần mỗi môn theo Loại lớp (Chọn/Thường) ×
// Khối × Học kỳ, chi tiết CK (chính khoá) / TC (tự chọn) / CĐ (chuyên đề).
// MỖI TUẦN TKB CÓ 1 BẢN RIÊNG — phải chọn tuần trước khi xem/sửa.
const HOCKY = [{ k: "hk1", label: "Học kỳ 1" }, { k: "hk2", label: "Học kỳ 2" }];
const LOAI = [{ k: "chon", label: "Lớp chọn" }, { k: "thuong", label: "Lớp thường" }];
const KHOIS = ["10", "11", "12"];
// Môn không xếp trong bảng số tiết văn hoá.
const EXCLUDE = ["pháp luật", "lập trình thiết kế web"];
// DD/MM
const fmtDM = (d) => {
  if (!d) return "";
  const p = String(d).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : d;
};

const HappySchoolPeriodRules = () => {
  const schoolId = getActiveSchoolId();
  const [weeks, setWeeks] = useState([]);
  const [weekId, setWeekId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [rules, setRules] = useState({});
  const [hocky, setHocky] = useState("hk1");
  const [loai, setLoai] = useState("chon");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!schoolId) return;
    Promise.all([listSubjects(schoolId), listTimetableWeeks(schoolId)])
      .then(([subs, ws]) => {
        setSubjects((subs || []).filter((s) => !EXCLUDE.includes(String(s.name).toLowerCase().trim())));
        const sorted = (ws || []).slice().sort((a, b) => (b.startDate || "").localeCompare(a.startDate || ""));
        setWeeks(sorted);
        if (sorted.length) setWeekId(sorted[0].id);
        else setLoading(false);
      })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId || !weekId) return;
    setLoading(true);
    setMsg("");
    getPeriodRules(schoolId, weekId)
      .then((r) => setRules(r || {}))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, weekId]);

  const cellVal = (khoi, sid, field) => {
    const v = (((rules[hocky] || {})[loai] || {})[khoi] || {})[sid];
    return v && v[field] != null ? v[field] : "";
  };
  const setCell = (khoi, sid, field, raw) => {
    const val = raw === "" ? "" : Math.max(0, Number(raw) || 0);
    setRules((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const hk = (next[hocky] = next[hocky] || {});
      const lp = (hk[loai] = hk[loai] || {});
      const kh = (lp[khoi] = lp[khoi] || {});
      const cell = (kh[sid] = kh[sid] || { ck: "", tc: "", cd: "" });
      cell[field] = val;
      return next;
    });
    setMsg("");
  };
  const total = (khoi, sid) => ["ck", "tc", "cd"].reduce((s, f) => s + (Number(cellVal(khoi, sid, f)) || 0), 0);

  const save = async () => {
    setSaving(true); setError(""); setMsg("");
    try { await setPeriodRules(schoolId, weekId, rules); setMsg("Đã lưu bảng số tiết."); }
    catch (e) { setError(e.message); } finally { setSaving(false); }
  };
  const apply = async () => {
    setApplying(true); setError(""); setMsg("");
    try {
      await setPeriodRules(schoolId, weekId, rules); // lưu trước rồi áp dụng
      const r = await applyPeriodRules(schoolId, weekId, hocky, "total");
      let m = `Đã áp dụng ${r.applied} ô (periodsPerWeek = tổng CK+TC+CĐ) cho ${HOCKY.find((h) => h.k === hocky).label}.`;
      if (r.missing && r.missing.length) m += ` ⚠ ${r.missing.length} môn có trong quy định nhưng lớp chưa phân công GV (bỏ qua): ` + r.missing.slice(0, 12).map((x) => `${x.className}`).join(", ") + (r.missing.length > 12 ? "..." : "");
      setMsg(m);
    } catch (e) { setError(e.message); } finally { setApplying(false); }
  };

  const sortedSubjects = useMemo(() => subjects.slice().sort((a, b) => a.name.localeCompare(b.name, "vi")), [subjects]);

  if (!schoolId) return <div style={{ padding: 16 }}>Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link>.</div>;
  if (weeks.length === 0 && !loading) return <div style={{ padding: 16, color: "#6b7280" }}>Chưa có tuần TKB nào — tạo tuần trước ở "Xếp thời khoá biểu".</div>;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 4px" }}>🔢 Số tiết</h2>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Quy định số tiết/tuần mỗi môn theo <b>Loại lớp</b> × <b>Khối</b> × <b>Học kỳ</b> — <b>riêng cho từng tuần TKB</b>. Mỗi ô gồm <b>CK</b> (chính khoá) · <b>TC</b> (tự chọn) · <b>CĐ</b> (chuyên đề). Bấm <b>Áp dụng</b> để ghi tổng (CK+TC+CĐ) vào số tiết xếp TKB của tất cả lớp.
      </p>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Tuần TKB:</span>
        <select value={weekId} onChange={(e) => setWeekId(e.target.value)} style={selectStyle}>
          {weeks.map((w) => (
            <option key={w.id} value={w.id}>Tuần {fmtDM(w.startDate)}–{fmtDM(w.endDate)}</option>
          ))}
        </select>
        <span style={{ width: 12 }} />
        <span style={{ fontSize: 13, fontWeight: 600 }}>Học kỳ:</span>
        {HOCKY.map((h) => (
          <button key={h.k} onClick={() => setHocky(h.k)} style={hocky === h.k ? pill(true) : pill(false)}>{h.label}</button>
        ))}
        <span style={{ width: 12 }} />
        <span style={{ fontSize: 13, fontWeight: 600 }}>Loại lớp:</span>
        {LOAI.map((l) => (
          <button key={l.k} onClick={() => setLoai(l.k)} style={loai === l.k ? pill(true) : pill(false)}>{l.label}</button>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={save} disabled={saving || loading} style={ghost}>{saving ? "Đang lưu..." : "💾 Lưu"}</button>
          <button onClick={apply} disabled={applying || loading} style={primary}>{applying ? "Đang áp dụng..." : "✅ Áp dụng vào các lớp"}</button>
        </div>
      </div>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {msg && <p style={{ color: "#059669", fontSize: 13 }}>{msg}</p>}
      {loading && <p style={{ color: "#6b7280" }}>Đang tải...</p>}
      {!loading && (
      <>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 13, background: "#fff" }}>
          <thead>
            <tr>
              <th rowSpan={2} style={{ ...th, textAlign: "left", minWidth: 140 }}>Môn</th>
              {KHOIS.map((k) => (<th key={k} colSpan={4} style={th}>Khối {k}</th>))}
            </tr>
            <tr>
              {KHOIS.map((k) => (
                <React.Fragment key={k}>
                  <th style={thSub}>CK</th><th style={thSub}>TC</th><th style={thSub}>CĐ</th><th style={{ ...thSub, background: "#ecfdf5" }}>Tổng</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedSubjects.map((s) => (
              <tr key={s.id}>
                <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{s.name}</td>
                {KHOIS.map((k) => (
                  <React.Fragment key={k}>
                    {["ck", "tc", "cd"].map((f) => (
                      <td key={f} style={td}>
                        <input type="number" min={0} value={cellVal(k, s.id, f)} onChange={(e) => setCell(k, s.id, f, e.target.value)}
                          style={inp} />
                      </td>
                    ))}
                    <td style={{ ...td, background: "#ecfdf5", fontWeight: 700 }}>{total(k, s.id) || ""}</td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>Để 0/trống nghĩa là loại lớp/khối đó không học môn này. Nhớ bấm "Áp dụng" sau khi sửa để cập nhật số tiết xếp TKB.</p>
      </>
      )}
    </div>
  );
};

const selectStyle = { padding: "6px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, background: "#fff" };
const pill = (active) => ({ padding: "6px 14px", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: active ? 700 : 500, border: active ? "1px solid #0ea5a3" : "1px solid #d1d5db", background: active ? "#0ea5a3" : "#fff", color: active ? "#fff" : "#374151" });
const primary = { padding: "7px 14px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 };
const ghost = { padding: "7px 14px", background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, cursor: "pointer", fontSize: 13 };
const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", whiteSpace: "nowrap" };
const thSub = { ...th, fontSize: 11, color: "#6b7280", padding: "3px 6px" };
const td = { border: "1px solid #e5e7eb", padding: "3px 5px", textAlign: "center" };
const inp = { width: 42, padding: "4px 3px", border: "1px solid #d1d5db", borderRadius: 5, textAlign: "center", fontSize: 13 };

export default HappySchoolPeriodRules;
